/** Pure template-string renderers for usage-tier account states.
 *
 * Layer: `view` per docs/design/spa-account-extraction-260524.md §3.2.
 *
 * Covers:
 *   - anonymous + individual + cap-hit states (the "usage-tier" surfaces)
 *   - renderAccountInfoSection + renderUserClassBadge helpers
 *   - creditBalanceCardHtml + renderBuyMoreSection helpers
 *   - renderTokenTxSection + renderPricingExplainerSection
 *   - fmtDate / fmtMonth / nextMonthFirst date helpers (exported for cross-view use)
 *
 * Purity contract: every exported function accepts typed data and returns a
 * string. No DOM access, no fetch, no window.X mutations — consumers do
 * `el.innerHTML = render*(...)`.
 *
 * Extracted from web/static/account.html in Phase AC.4.
 */
import { pickPersonalGroup, creditBalanceOf } from './model.js';
// ─── Date helpers (exported for cross-view use) ───────────────────────────
export function fmtDate(iso) {
    if (!iso)
        return '—';
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: 'numeric', month: 'short', day: 'numeric',
        });
    }
    catch (_e) {
        return iso;
    }
}
export function fmtMonth(date) {
    try {
        return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
    }
    catch (_e) {
        return '';
    }
}
export function nextMonthFirst(date) {
    return new Date(date.getFullYear(), date.getMonth() + 1, 1);
}
// ─── User-class badge ─────────────────────────────────────────────────────
export function renderUserClassBadge(esc, userClass) {
    const cls = (userClass === 'institutional') ? 'institutional'
        : (userClass === 'anonymous') ? 'free'
            : 'individual';
    const label = (userClass === 'institutional') ? 'Institutional'
        : (userClass === 'anonymous') ? 'Anonymous'
            : 'Individual';
    return `<span class="tier-badge ${esc(cls)}" data-testid="tier-badge-${esc(cls)}">${esc(label)}</span>`;
}
export function renderAccountInfoSection(esc, user, opts) {
    const o = opts || {};
    const tier = renderUserClassBadge(esc, o.userClass || 'individual');
    const rows = [
        `<div class="field-row"><span class="field-label">Email</span>
          <span class="field-value">${esc(user.email || '—')}</span></div>`,
        `<div class="field-row"><span class="field-label">Account class</span>
          <span class="field-value">${tier}</span></div>`,
    ];
    if (o.institutionName) {
        rows.push(`<div class="field-row"><span class="field-label">Institution</span>
            <span class="field-value">${esc(o.institutionName)}</span></div>`);
    }
    if (o.billingMode) {
        rows.push(`<div class="field-row"><span class="field-label">Billing mode</span>
            <span class="field-value" style="color:var(--text-muted);font-style:italic">${esc(o.billingMode)}</span></div>`);
    }
    rows.push(`<div class="field-row"><span class="field-label">Member since</span>
        <span class="field-value">${esc(fmtDate(user.created_at))}</span></div>`);
    return `<section class="account-section" aria-labelledby="profile-h">
        <h2 id="profile-h">Account Info</h2>${rows.join('')}</section>`;
}
// ─── Credit balance card ──────────────────────────────────────────────────
// Beta: the balance is the internal quota unit, surfaced to users as "free Beta
// pages remaining". No purchase surface — the buy button, the $ footnote, and the
// commercialization-gate branch were REMOVED when the site went to a free Beta for
// everyone (Epic beta-billing-purdue-unlimited-no-dollars-260821 Phase 3, U2). This
// card renders no $, no price, and no "buy" CTA regardless of any config flag.
export function creditBalanceCardHtml(esc, balance) {
    const balText = (balance == null) ? '—' : String(balance);
    const lowBal = (balance != null && balance <= 10);
    const exhausted = (balance != null && balance === 0);
    const cardCls = exhausted ? ' exhausted' : '';
    const valCls = exhausted ? ' exhausted' : (lowBal ? ' low' : '');
    return `<div class="credit-balance-card${cardCls}" data-testid="credit-balance">
        <div class="credit-balance-label">Free Beta pages remaining</div>
        <div class="credit-balance-value${valCls}" data-testid="credit-balance-value"
             aria-label="${esc(balText)} free Beta pages remaining">${esc(balText)}</div>
        <p class="credit-balance-footnote">
            Remediation is free during the Beta — no payment required.
        </p>
    </div>`;
}
// ─── Token-transactions section shell ────────────────────────────────────
export function renderTokenTxSection() {
    return `<section class="account-section" data-testid="token-transactions" aria-labelledby="tx-h">
        <h2 id="tx-h">Recent Credit Activity</h2>
        <div id="token-tx-body">
            <p style="font-size:12px;color:var(--text-muted)">Loading…</p>
        </div>
    </section>`;
}
// ─── State renderers ──────────────────────────────────────────────────────
export function renderAnonymousState(esc, data) {
    const anon = data.anonymous || {};
    const free = (typeof anon.free_remaining === 'number') ? anon.free_remaining : 100;
    const limit = (typeof anon.free_limit === 'number') ? anon.free_limit : 100;
    const used = Math.max(0, limit - free);
    const pct = limit > 0 ? Math.round((free / limit) * 100) : 0;
    return `
        <div class="not-logged-in" role="status" data-testid="account-anonymous">
            <p style="text-align:center;font-size:18px;font-weight:700;color:var(--text);margin-bottom:8px">You are browsing as a guest</p>
            <p style="text-align:center;font-size:14px;margin-bottom:24px">Free usage — no sign-in required.</p>

            <div class="anon-quota-card" role="region" aria-label="Free page quota">
                <div class="anon-quota-number" aria-label="${free} of ${limit} free pages remaining">${esc(free)}</div>
                <div class="anon-quota-denom">/ ${esc(limit)} free pages remaining</div>
                <div class="anon-quota-label">Resets every 24 hours</div>
                <div class="progress-track" role="progressbar"
                     aria-valuenow="${free}" aria-valuemin="0" aria-valuemax="${limit}"
                     aria-label="Free page quota: ${free} of ${limit} remaining">
                    <div class="progress-fill" style="width: ${pct}%"></div>
                </div>
                <p style="font-size:13px;color:var(--text-muted);margin-top:12px">
                    Used ${esc(used)} pages in the last 24 hours.
                </p>
                <a href="/" class="account-btn primary" style="justify-content:center;margin-top:16px;font-size:14px">
                    Log in with a Purdue email using the Microsoft button
                </a>
                <p class="plan-note" style="margin-top:8px">
                    Purdue accounts get unlimited access. Any sign-in gives you a fresh free Beta allowance.
                </p>
            </div>

            <div class="anon-what-explainer" role="complementary" aria-label="How the free Beta works">
                <h3>How the free Beta works</h3>
                <p>
                    DocAble remediates documents for accessibility (PDF/UA, PPTX, DOCX, XLSX).
                    It is <strong>free during the Beta</strong> &mdash; no payment, no subscription.
                </p>
                <ul style="padding-left:20px;margin-top:10px;line-height:1.7">
                    <li>Guests get a free daily page allowance.</li>
                    <li>Log in with a Purdue email using the Microsoft button for unlimited access.</li>
                </ul>
            </div>
        </div>`;
}
export function renderIndividualState(esc, data) {
    const u = (data.user || {});
    const personal = pickPersonalGroup(data);
    const credits = creditBalanceOf(personal, data.user);
    return `
        <div class="header-row-grid">
            ${renderAccountInfoSection(esc, u, { userClass: 'individual' })}
            <section class="account-section" data-testid="account-tokens" aria-labelledby="tokens-h">
                <h2 id="tokens-h">Free Beta Pages</h2>
                ${creditBalanceCardHtml(esc, credits)}
                <p class="plan-note" style="margin-top:12px">
                    You are on the free Beta. Log in with a Purdue email using the Microsoft button for unlimited access.
                </p>
            </section>
        </div>
        ${renderTokenTxSection()}
    `;
}
export function renderCapHitState(esc, data) {
    const u = (data.user || {});
    const flatGroup = (data.groups || [])
        .find(g => g != null && g.type === 'institutional') || {};
    const personal = pickPersonalGroup(data);
    const credits = creditBalanceOf(personal, data.user);
    const usage = data.monthly_usage || {};
    const cur = new Date();
    const resetDate = nextMonthFirst(cur);
    const resetIso = resetDate.toISOString();
    const cap = usage.credit_cap || 0;
    const used = usage.credit_used || cap;
    return `
        <div class="banner-error" role="alert" aria-live="assertive" aria-label="Credit cap warning" data-testid="cap-hit-banner">
            You've used ${esc(used)} of ${esc(cap)} credits this month.
            New remediations are paused until your cap resets on ${esc(fmtDate(resetIso))}.
            Contact your institution admin for an immediate increase.
        </div>
        <div class="header-row-grid">
            ${renderAccountInfoSection(esc, u, { userClass: 'institutional', institutionName: flatGroup.name || '' })}
            <section class="account-section" data-testid="account-tokens" aria-labelledby="cap-tokens-h">
                <h2 id="cap-tokens-h">Group Access</h2>
                <div class="institutional-unlimited-panel" role="status">
                    <span class="tier-badge institutional" style="margin-bottom:8px;display:inline-block">Institutional</span>
                    <p style="font-size:14px;margin-top:6px">
                        You have access via <strong>${esc(flatGroup.name || 'your institution')}</strong>.
                    </p>
                </div>
            </section>
        </div>
        ${renderMonthlyUsageSectionInternal(esc, usage, `${esc(fmtMonth(cur))} — resets on ${esc(fmtDate(resetIso))}`, true)}
        <section class="account-section">
            <div class="banner-info" role="note">
                <strong>Options:</strong>
                <ul style="padding-left:18px;margin-top:6px;font-size:13px;line-height:1.7">
                    <li>Wait for your cap to reset on <strong>${esc(fmtDate(resetIso))}</strong></li>
                    <li>Contact your institution admin to request a per-user cap increase</li>
                    <li>Use your personal balance to process files against your own allocation (not the group pool)</li>
                </ul>
            </div>
        </section>
        ${renderPersonalBalanceSectionInternal(esc, credits, { capBypass: true })}
        ${renderTokenTxSection()}
    `;
}
export function renderMonthlyUsageSectionInternal(esc, usage, headerLine, isCapHit) {
    const used = usage.credit_used || 0;
    const cap = usage.credit_cap;
    let body;
    if (cap != null) {
        const pct = cap > 0 ? Math.round((used / cap) * 100) : 0;
        const cls = isCapHit ? 'error' : (pct > 80 ? 'warning' : '');
        const labelStyle = isCapHit ? 'color:var(--error);font-weight:700' : '';
        const labelText = isCapHit ? 'Credits — cap reached' : 'Credits used';
        const countText = `${used} / ${cap} credits (${pct}%)`;
        const id = 'credit-usage-' + Math.random().toString(36).slice(2, 7);
        body = `<div class="usage-row">
            <div class="usage-row-header">
                <span class="usage-row-label" style="${labelStyle}">${esc(labelText)}</span>
                <span class="usage-row-count" id="${id}">${esc(countText)}</span>
            </div>
            <div class="progress-track" role="progressbar"
                 aria-valuenow="${used}" aria-valuemin="0" aria-valuemax="${cap}"
                 aria-labelledby="${id}">
                <div class="progress-fill ${cls}" style="width: ${Math.min(100, pct)}%"></div>
            </div>
        </div>`;
    }
    else {
        body = '<p style="font-size:13px;color:var(--text-muted)">No per-user cap set.</p>';
    }
    return `<section class="account-section" data-testid="monthly-usage" aria-labelledby="usage-h">
        <h2 id="usage-h">Your Usage This Month</h2>
        <p style="font-size:13px;color:var(--text-muted);margin-bottom:16px">${headerLine}</p>
        ${body}
        <p class="plan-note" style="margin-top:4px">
            Per-user monthly cap set by your institution admin. Contact your admin for cap increases.
            PDF pages cost 2 credits each; Office pages cost 1 credit each.
        </p>
    </section>`;
}
export function renderPersonalBalanceSectionInternal(esc, credits, opts) {
    const o = opts || {};
    const allowBuy = !!o.allowBuy;
    const note = o.capBypass
        ? 'Personal credits bypass your monthly group cap. Upload and choose "Use my own credits" to draw from this balance.'
        : (allowBuy
            ? 'Your personal balance is used only after your group allocation is exhausted, or when you choose "use my own credits" at upload.'
            : 'Your personal balance remains available as a fallback under your institution\'s contract billing arrangement.');
    return `<section class="account-section" data-testid="account-tokens-personal" aria-labelledby="personal-h">
        <h2 id="personal-h">Personal Credit Balance</h2>
        <p style="font-size:13px;color:var(--text-muted);margin-bottom:12px">${esc(note)}</p>
        ${creditBalanceCardHtml(esc, credits)}
        <p class="plan-note" style="margin-top:10px">
            ${allowBuy ? 'Personal credits never expire. Group allocation draws first by default.'
        : 'Contact your institution admin to adjust capacity.'}
        </p>
    </section>`;
}
